import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import {
  Settings, Shield, Users, Store, DollarSign, Building2, CalendarClock,
  Bell, Activity, Database, Download, Paintbrush, AlertCircle, Gauge,
  Key, Layers, CreditCard, HelpCircle, Crown, BarChart3, Camera, Package,
  FileWarning, AlertTriangle, Handshake, ChevronRight,
} from 'lucide-react';

const SECTIONS = [
  {
    title: 'User Management',
    accent: 'bg-stone-600',
    items: [
      { path: '/users', icon: Users, label: 'Users', desc: 'Manage system users', color: 'text-muted-foreground', bg: 'bg-muted/50', ring: 'ring-border' },
      { path: '/role-management', icon: Shield, label: 'Roles & Permissions', desc: 'Configure access control', color: 'text-info', bg: 'bg-info/10', ring: 'ring-blue-100' },
    ],
  },
  {
    title: 'Organization',
    accent: 'bg-success',
    items: [
      { path: '/branches', icon: Store, label: 'Branches', desc: 'Manage branch locations', color: 'text-success', bg: 'bg-success/10', ring: 'ring-emerald-100' },
      { path: '/assets', icon: Package, label: 'Assets & Liabilities', desc: 'Company asset tracking', color: 'text-info', bg: 'bg-info/10', ring: 'ring-blue-100' },
      { path: '/documents', icon: FileWarning, label: 'Documents', desc: 'Company documents', color: 'text-warning', bg: 'bg-warning/10', ring: 'ring-amber-100' },
      { path: '/fines', icon: AlertTriangle, label: 'Fines & Penalties', desc: 'Track fines', color: 'text-error', bg: 'bg-error/10', ring: 'ring-red-100' },
      { path: '/partners', icon: Handshake, label: 'Partners', desc: 'Business partnerships', color: 'text-violet-600', bg: 'bg-violet-50', ring: 'ring-violet-100' },
      { path: '/company-loans', icon: Building2, label: 'Company Loans', desc: 'Corporate loan management', color: 'text-indigo-600', bg: 'bg-indigo-50', ring: 'ring-indigo-100' },
    ],
  },
  {
    title: 'System Configuration',
    accent: 'bg-info',
    items: [
      { path: '/settings', icon: Settings, label: 'Settings', desc: 'General system settings', color: 'text-muted-foreground', bg: 'bg-muted/50', ring: 'ring-border' },
      { path: '/currency-settings', icon: DollarSign, label: 'Currency', desc: 'Currency & exchange rates', color: 'text-success', bg: 'bg-success/10', ring: 'ring-emerald-100' },
      { path: '/branding', icon: Building2, label: 'Branding & PDF', desc: 'Logo, colors & PDF layout', color: 'text-info', bg: 'bg-info/10', ring: 'ring-blue-100' },
      { path: '/white-label', icon: Paintbrush, label: 'White-Label', desc: 'Custom branding for clients', color: 'text-violet-600', bg: 'bg-violet-50', ring: 'ring-violet-100' },
      { path: '/modules', icon: Layers, label: 'Modules', desc: 'Enable/disable modules', color: 'text-indigo-600', bg: 'bg-indigo-50', ring: 'ring-indigo-100' },
    ],
  },
  {
    title: 'Monitoring & Security',
    accent: 'bg-warning',
    items: [
      { path: '/activity-logs', icon: Activity, label: 'Activity Logs', desc: 'User activity tracking', color: 'text-muted-foreground', bg: 'bg-muted/50', ring: 'ring-border' },
      { path: '/audit-trail', icon: Shield, label: 'Audit Trail', desc: 'Detailed audit history', color: 'text-info', bg: 'bg-info/10', ring: 'ring-blue-100' },
      { path: '/rate-limits', icon: Gauge, label: 'Rate Limits', desc: 'API usage & limits', color: 'text-warning', bg: 'bg-warning/10', ring: 'ring-amber-100' },
      { path: '/usage-alerts', icon: AlertCircle, label: 'Usage & Limits', desc: 'Resource usage monitoring', color: 'text-error', bg: 'bg-error/10', ring: 'ring-red-100' },
      { path: '/cctv', icon: Camera, label: 'CCTV Security', desc: 'Video surveillance', color: 'text-muted-foreground', bg: 'bg-muted/50', ring: 'ring-border' },
      { path: '/task-compliance', icon: Shield, label: 'Compliance', desc: 'Task & regulatory compliance', color: 'text-info', bg: 'bg-info/10', ring: 'ring-blue-100' },
    ],
  },
  {
    title: 'Automation & Integration',
    accent: 'bg-indigo-500',
    items: [
      { path: '/scheduled-reports', icon: CalendarClock, label: 'Scheduled Reports', desc: 'Automated report delivery', color: 'text-info', bg: 'bg-info/10', ring: 'ring-blue-100' },
      { path: '/task-reminders', icon: Bell, label: 'Task Reminders', desc: 'Automated task alerts', color: 'text-warning', bg: 'bg-warning/10', ring: 'ring-amber-100' },
      { path: '/integrations', icon: Key, label: 'API Keys', desc: 'API key management', color: 'text-muted-foreground', bg: 'bg-muted/50', ring: 'ring-border' },
      { path: '/data-management', icon: Database, label: 'Data Management', desc: 'Database operations', color: 'text-indigo-600', bg: 'bg-indigo-50', ring: 'ring-indigo-100' },
      { path: '/data-export', icon: Download, label: 'Data Export', desc: 'Export system data', color: 'text-muted-foreground', bg: 'bg-muted/50', ring: 'ring-border' },
      { path: '/subscription', icon: CreditCard, label: 'Subscription', desc: 'Plan & billing', color: 'text-success', bg: 'bg-success/10', ring: 'ring-emerald-100' },
    ],
  },
  {
    title: 'Platform',
    accent: 'bg-warning',
    items: [
      { path: '/super-admin', icon: Crown, label: 'Platform Admin', desc: 'Super admin controls', color: 'text-warning', bg: 'bg-warning/10', ring: 'ring-amber-100' },
      { path: '/analytics-dashboard', icon: BarChart3, label: 'Platform Analytics', desc: 'Platform-wide stats', color: 'text-violet-600', bg: 'bg-violet-50', ring: 'ring-violet-100' },
      { path: '/help', icon: HelpCircle, label: 'Help & Guide', desc: 'Documentation & support', color: 'text-muted-foreground', bg: 'bg-muted/50', ring: 'ring-border' },
    ],
  },
];

function FeatureCard({ item, navigate }) {
  return (
    <button
      onClick={() => navigate(item.path)}
      className="group flex items-center gap-3.5 p-4 rounded-xl bg-white border border-border hover:border-border hover:shadow-md transition-all duration-200 text-left"
      data-testid={`nav-${item.path.replace('/', '')}`}
    >
      <div className={`w-10 h-10 rounded-xl ${item.bg} ${item.color} flex items-center justify-center shrink-0 ring-1 ${item.ring} group-hover:scale-110 transition-transform duration-200`}>
        <item.icon size={18} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-foreground text-sm group-hover:text-foreground transition-colors">{item.label}</p>
        <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{item.desc}</p>
      </div>
      <ChevronRight size={14} className="text-stone-200 group-hover:text-muted-foreground shrink-0 group-hover:translate-x-0.5 transition-all" />
    </button>
  );
}

export default function AdminModulePage() {
  const navigate = useNavigate();
  return (
    <DashboardLayout module="admin">
      <div className="space-y-10 max-w-6xl mx-auto" data-testid="admin-module-page">
        {/* Header */}
        <div className="relative">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-stone-700 to-stone-900 flex items-center justify-center shadow-lg shadow-stone-300/50">
              <Settings size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground">Administration</h1>
              <p className="text-sm text-muted-foreground mt-0.5">System settings, users, security & compliance</p>
            </div>
          </div>
        </div>

        {/* Sections */}
        {SECTIONS.map((section, si) => (
          <div key={section.title} style={{ animationDelay: `${si * 60}ms` }}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-1 h-5 rounded-full ${section.accent}`} />
              <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{section.title}</h2>
              <div className="flex-1 h-px bg-muted" />
              <span className="text-[10px] text-muted-foreground font-medium">{section.items.length} items</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {section.items.map((item) => (
                <FeatureCard key={item.path} item={item} navigate={navigate} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}
